import { Outlet, useParams } from "react-router-dom";
import { resolveStateRoute, getState, ROUTE_TRACK } from "@/lib/stateRegistry";
import { SHOW_BUSINESS_TRACK, SHOW_OTHER_STATES, PUBLIC_LAUNCH_STATE_CODE } from "@/lib/displayFlags";
import NotFound from "@/components/NotFound";
import WaitlistPage from "@/components/WaitlistPage";

/**
 * Layout route for /:stateSlug/*. Unknown slugs (NY, NJ, /ny, anything not in
 * STATE_REGISTRY) render the 404 page. Children are guarded per track/feature by
 * <Guard segment="...">, which is a convenience only; the API is the enforcement point.
 *
 * Display-flag block (Owner, 2026-09-22): while SHOW_OTHER_STATES is false, any state
 * other than the public launch state (Texas) renders the SAME 404 page an unknown slug
 * gets, for every sub-route including the bare landing (/ohio) and direct navigation —
 * not a distinct "coming soon"/blocked page, which would itself be an expansion signal.
 * STATE_REGISTRY, its coming_soon status and launch order are untouched; flipping
 * SHOW_OTHER_STATES back to true removes this block with no other code change.
 */
export function StateGate() {
  const { stateSlug } = useParams();
  const state = getState(stateSlug);
  if (!state) return <NotFound />;
  if (!SHOW_OTHER_STATES && state.code !== PUBLIC_LAUNCH_STATE_CODE) return <NotFound />;
  return <Outlet />;
}

/**
 * Wrap a state sub-route. `segment` is the first path segment after the state slug.
 *
 * Display-flag block (Owner, 2026-09-22): while SHOW_BUSINESS_TRACK is false, the
 * commercial-track segment (any state, Texas included) renders the same 404 as an
 * unknown route, before the registry is even consulted. Flipping SHOW_BUSINESS_TRACK
 * back to true removes this block with no other code change.
 */
export function Guard({ segment, children }) {
  const { stateSlug } = useParams();
  if (!SHOW_BUSINESS_TRACK && ROUTE_TRACK[segment] === "commercial") return <NotFound />;
  const resolved = resolveStateRoute(stateSlug, segment);
  if (resolved.kind === "not_found") return <NotFound />;
  if (resolved.kind === "waitlist") return <WaitlistPage state={getState(stateSlug)} track={resolved.track} />;
  return children;
}
